/**
 * Press It -- Vinyl Crowdfund
 * press-it-crowdfund.js  v2.0
 *
 * New in v2:
 *  - Live countdown timer (DD HH MM SS), ticking every second
 *  - Reads data-end-iso (ISO 8601 date string) from root element
 *  - Auto-expires campaign in the UI when countdown hits zero
 */

(function () {
  'use strict';

  const SEL = {
    root:          '[data-press-it]',
    arc:           '[data-pi-arc]',
    fill:          '[data-pi-fill]',
    pctLabel:      '[data-pi-pct]',
    barCount:      '[data-pi-bar-count]',
    statOrders:    '[data-pi-stat-orders]',
    statRemaining: '[data-pi-stat-remaining]',
    statDays:      '[data-pi-stat-days]',
    statusMsg:     '[data-pi-status]',
    fundedBadge:   '[data-pi-funded-badge]',
    endedBanner:   '[data-pi-ended-banner]',
    preorderBtn:   '[data-pi-preorder-btn]',
    btnText:       '[data-pi-btn-text]',
    qtyMinus:      '[data-pi-qty-minus]',
    qtyPlus:       '[data-pi-qty-plus]',
    qtyDisplay:    '[data-pi-qty-display]',
    notifySection: '[data-pi-notify]',
    notifyInput:   '[data-pi-notify-input]',
    notifyBtn:     '[data-pi-notify-btn]',
    discSvg:       '[data-pi-disc-svg]',
    // Per-variant campaign switching + two-number progress
    variantSelect: '[data-pi-variant-select]',
    variantsJson:  '[data-pi-variants]',
    beMarker:      '[data-pi-be-marker]',
    beLabel:       '[data-pi-be-label]',
    terms:         '[data-pi-terms]',
    barLabel:      '[data-pi-bar-label]',
    track:         '[data-pi-track]',
    statRemLabel:  '[data-pi-stat-remaining-label]',
    price:         '.press-it__price',
    // Countdown
    cdDays:        '[data-pi-cd-days]',
    cdHours:       '[data-pi-cd-hours]',
    cdMins:        '[data-pi-cd-mins]',
    cdSecs:        '[data-pi-cd-secs]',
    cdWrap:        '[data-pi-countdown]',
    cdCells:       '[data-pi-cd-cells]',
    cdEndedMsg:    '[data-pi-cd-ended]',
  };

  const CIRCUMFERENCE = 2 * Math.PI * 52;

  /**
   * Campaign state for a single variant.
   *
   * Two numbers, not one. `breakEven` is the GO threshold — the point at which the
   * record is definitely pressed. `campaignQty` is the ceiling — how many copies
   * exist to sell. Passing break-even makes the campaign a success WITHOUT closing
   * it: pre-orders keep counting, and what happens next depends on inventoryMode.
   *
   *   running          count < breakEven, still open
   *   secured          count >= breakEven — funded, still selling
   *   closed_sold_out  capped, and every copy is claimed
   *   closed_ended     end date passed with break-even met
   *   failed           end date passed without it
   *
   * Keep in sync with the pi_state block in sections/product-page.liquid, which
   * renders the same thing server-side so there is no flash of wrong state.
   */
  function campaignState(v, ended) {
    const breakEven   = Math.max(0, v.breakEven | 0);
    const campaignQty = Math.max(breakEven, v.campaignQty | 0);
    const count       = Math.max(0, v.count | 0);
    const secured     = breakEven > 0 && count >= breakEven;
    const atCeiling   = v.inventoryMode !== 'oversell' && count >= campaignQty;

    let state;
    if (secured && atCeiling)   state = 'closed_sold_out';
    else if (ended && secured)  state = 'closed_ended';
    else if (ended)             state = 'failed';
    else if (secured)           state = 'secured';
    else                        state = 'running';

    return {
      state, secured, breakEven, campaignQty, count,
      // The whole pressing, which is NOT the same as the copies offered here: under
      // Option B the artist buys part of the run upfront, so campaignQty < pressQty.
      // Break-even only secures the pressing — the run itself is already decided.
      pressQty:  Math.max(campaignQty, v.pressQty | 0),
      oversell:  v.inventoryMode === 'oversell',
      needed:    Math.max(0, breakEven - count),
      available: Math.max(0, campaignQty - count),
      // Bar spans 0…campaignQty and is never rescaled at break-even; the marker
      // sits where break-even falls along it.
      barPct:    campaignQty > 0 ? Math.min(100, (count / campaignQty) * 100) : 0,
      bePct:     campaignQty > 0 ? Math.min(100, (breakEven / campaignQty) * 100) : 0,
      // The dial answers a different question: how close to happening at all.
      dialPct:   breakEven > 0 ? Math.min(1, count / breakEven) : 1,
      sellable:  state === 'running' || state === 'secured',
    };
  }

  // The campaign's terms, stated plainly and in every state. Fans could see a break-even
  // and a claimed count but never the size of the pressing, and the cap choice — whether
  // sales stop at the ceiling or run to the end date — only surfaced after break-even.
  function termsHtml(s) {
    const bits = [s.pressQty.toLocaleString() + '-copy pressing'];
    if (s.pressQty > s.campaignQty) {
      bits.push(s.campaignQty.toLocaleString() + ' offered here');
    }
    bits.push(s.breakEven.toLocaleString() + ' secure it');
    bits.push(s.oversell
      ? 'pre-orders keep counting after that, until the campaign closes'
      : 'sales stop once all ' + s.campaignQty.toLocaleString() + ' are claimed');
    return bits.join(' · ');
  }
  function statusHtml(s, mode, ship) {
    const shipTxt = ship || '8-10 weeks';
    switch (s.state) {
      case 'secured':
        return '<strong>Funded — going to press.</strong> Break-even passed at ' + s.breakEven +
          ' pre-orders, so this record is confirmed. ' +
          (mode === 'oversell'
            ? 'Still open until the campaign closes. '
            : s.available + ' of ' + s.campaignQty + ' copies still available. ') +
          'Expect dispatch in ' + shipTxt + '.';
      case 'closed_sold_out':
        return '<strong>Funded and sold out.</strong> All ' + s.campaignQty +
          ' copies are claimed and the record is in production. Expect dispatch in ' + shipTxt + '.';
      case 'closed_ended':
        return '<strong>Funded — campaign closed.</strong> ' + s.count +
          ' pre-orders confirmed. This record is going to press.';
      case 'failed':
        return '<strong>Did not reach break-even.</strong> This campaign closed at ' + s.count +
          ' of the ' + s.breakEven + ' pre-orders needed. You are not charged — we\'ll be in touch ' +
          'about a refund or a relaunch.';
      default:
        return '<strong>' + s.needed + ' more to press this record.</strong> It goes to press once ' +
          s.breakEven + ' pre-orders are confirmed. If it doesn\'t get there, your card is never charged.';
    }
  }

  function pad(n) { return String(n).padStart(2, '0'); }

  function initPressIt(root) {
    const variantId    = root.dataset.variantId;
    const moq          = parseInt(root.dataset.moq      || '100', 10);
    const initialCount = parseInt(root.dataset.count    || '0',   10);
    const isActive     = root.dataset.active === 'true';
    const endIso       = root.dataset.endIso || '';
    let maxQtyRaw      = parseInt(root.dataset.maxQty   || '0',   10);
    let maxQty         = maxQtyRaw; // 0 = unlimited

    // Every variant's campaign numbers, so switching editions needs no round trip.
    let variants = [];
    try {
      const blob = document.querySelector(SEL.variantsJson);
      if (blob) variants = JSON.parse(blob.textContent) || [];
    } catch (e) { console.warn('[press-it] could not parse variant data', e); }

    // Fall back to the product-level numbers if the blob is missing or this product
    // predates the per-variant schema, so the widget degrades instead of blanking.
    function variantById(id) {
      return variants.find(v => String(v.id) === String(id)) || {
        id: id, breakEven: moq, campaignQty: moq, count: initialCount,
        inventoryMode: 'capped', available: true,
      };
    }

    let current = variantById(root.dataset.variantId);
    let count = current.count;
    let qty   = 1;
    let campaignActive = isActive;

    // -- DOM refs -------------------------------------------------
    const arc           = root.querySelector(SEL.arc);
    const fill          = root.querySelector(SEL.fill);
    const pctLabel      = root.querySelector(SEL.pctLabel);
    const barCount      = root.querySelector(SEL.barCount);
    const statOrders    = root.querySelector(SEL.statOrders);
    const statRemaining = root.querySelector(SEL.statRemaining);
    const statDays      = root.querySelector(SEL.statDays);
    const statusMsg     = root.querySelector(SEL.statusMsg);
    const fundedBadge   = root.querySelector(SEL.fundedBadge);
    const endedBanner   = root.querySelector(SEL.endedBanner);
    const preorderBtn   = root.querySelector(SEL.preorderBtn);
    const qtyMinus      = root.querySelector(SEL.qtyMinus);
    const qtyPlus       = root.querySelector(SEL.qtyPlus);
    const qtyDisplay    = root.querySelector(SEL.qtyDisplay);
    const notifySection = root.querySelector(SEL.notifySection);
    const notifyInput   = root.querySelector(SEL.notifyInput);
    const notifyBtn     = root.querySelector(SEL.notifyBtn);
    const discSvg       = root.querySelector(SEL.discSvg);
    const btnText       = root.querySelector(SEL.btnText);
    const beMarker      = root.querySelector(SEL.beMarker);
    const beLabel       = root.querySelector(SEL.beLabel);
    const barLabel      = root.querySelector(SEL.barLabel);
    const track         = root.querySelector(SEL.track);
    const statRemLabel  = root.querySelector(SEL.statRemLabel);
    const priceEl       = root.querySelector(SEL.price);
    const variantSelect = root.querySelector(SEL.variantSelect);

    // Countdown cells
    const cdDays     = root.querySelector(SEL.cdDays);
    const cdHours    = root.querySelector(SEL.cdHours);
    const cdMins     = root.querySelector(SEL.cdMins);
    const cdSecs     = root.querySelector(SEL.cdSecs);
    const cdWrap     = root.querySelector(SEL.cdWrap);
    const cdCells    = root.querySelector(SEL.cdCells);
    const cdEndedMsg = root.querySelector(SEL.cdEndedMsg);

    // Toast singleton
    let toast = document.querySelector('[data-pi-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'press-it__toast';
      toast.setAttribute('data-pi-toast', '');
      toast.setAttribute('aria-live', 'polite');
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }

    // ==========================================================
    //  COUNTDOWN TIMER
    // ==========================================================
    let countdownInterval = null;

    function tickCountdown() {
      if (!endIso || !cdDays) return;

      const endMs = new Date(endIso).getTime();
      const nowMs = Date.now();
      const diff  = endMs - nowMs;

      if (diff <= 0) {
        // Campaign expired in the browser -- expire the UI
        if (cdWrap) cdWrap.classList.add('is-ended');
        if (countdownInterval) clearInterval(countdownInterval);
        expireCampaign();
        return;
      }

      const totalSecs = Math.floor(diff / 1000);
      const days  = Math.floor(totalSecs / 86400);
      const hours = Math.floor((totalSecs % 86400) / 3600);
      const mins  = Math.floor((totalSecs % 3600) / 60);
      const secs  = totalSecs % 60;

      if (cdDays)  cdDays.textContent  = pad(days);
      if (cdHours) cdHours.textContent = pad(hours);
      if (cdMins)  cdMins.textContent  = pad(mins);
      if (cdSecs)  cdSecs.textContent  = pad(secs);

      // Update the "days left" stat to stay in sync
      if (statDays) statDays.textContent = days;

      // Urgency: red tint when <= 72 hours left
      const urgent = totalSecs <= 259200;
      [cdDays, cdHours, cdMins, cdSecs].forEach(el => {
        if (el) el.classList.toggle('is-urgent', urgent);
      });
    }

    function startCountdown() {
      if (!endIso || !cdDays) return;
      tickCountdown(); // immediate first tick
      countdownInterval = setInterval(tickCountdown, 1000);
    }

    function expireCampaign() {
      campaignActive = false;
      if (preorderBtn)   preorderBtn.style.display = 'none';
      // Re-render so the status flips to closed_ended or failed rather than staying
      // on "N more to press this record" under an ended banner.
      render(current.count);
      const s = campaignState(current, true);
      if (notifySection) notifySection.classList.toggle('is-visible', !s.secured);
      if (endedBanner) {
        endedBanner.classList.add('is-visible');
        endedBanner.textContent = s.secured
          ? 'Campaign closed — funded and going to press.'
          : 'This campaign has ended without reaching its target.';
      }
    }

    // ==========================================================
    //  RENDER -- update all progress indicators
    // ==========================================================
    function render(newCount) {
      count = newCount;
      current.count = newCount;

      // Ended is a property of the campaign as a whole (one end date per release),
      // not of the variant — so it comes from the countdown, not the blob.
      const ended = campaignEnded();
      const s = campaignState(current, ended);

      // Dial → progress toward break-even. Bar → progress toward the ceiling.
      if (arc) {
        arc.style.strokeDashoffset = CIRCUMFERENCE * (1 - s.dialPct);
        arc.style.stroke = s.secured ? '#4a9e6b' : '#d4a843';
      }
      if (pctLabel) pctLabel.textContent = Math.round(s.dialPct * 100) + '%';

      if (fill) {
        fill.style.width = s.barPct + '%';
        fill.classList.toggle('is-funded', s.secured);
      }
      if (beMarker) {
        beMarker.style.left = s.bePct + '%';
        beMarker.style.display = s.bePct >= 100 ? 'none' : '';
        beMarker.title = 'Break-even: ' + s.breakEven + ' pre-orders';
      }
      if (track) {
        track.setAttribute('aria-valuenow', s.count);
        track.setAttribute('aria-valuemax', s.campaignQty);
      }
      if (barLabel) {
        barLabel.textContent = s.secured
          ? 'Claimed of ' + s.campaignQty + ' available'
          : 'Progress to press this record';
      }
      if (beLabel) beLabel.textContent = 'Break-even ' + s.breakEven + ' · pressing ' + s.pressQty;
      const termsEl = root.querySelector(SEL.terms);
      if (termsEl) termsEl.textContent = termsHtml(s);

      if (barCount) {
        barCount.textContent = s.count;
        barCount.classList.toggle('is-funded', s.secured);
      }
      if (statOrders) {
        statOrders.textContent = s.count;
        statOrders.className = 'press-it__stat-val press-it__stat-val--' + (s.secured ? 'success' : 'amber');
      }
      if (statRemaining) {
        // Before break-even the useful number is "how many until it happens";
        // after it, "how many are left to buy".
        statRemaining.textContent = s.secured ? s.available : s.needed;
        statRemaining.className = 'press-it__stat-val' +
          (s.secured ? ' press-it__stat-val--success'
                     : (s.needed <= 20 ? ' press-it__stat-val--danger' : ''));
      }
      if (statRemLabel) statRemLabel.textContent = s.secured ? 'Still available' : 'Until press';

      if (statusMsg) {
        statusMsg.className = 'press-it__status is-' + s.state + (s.secured ? ' is-funded' : '');
        statusMsg.innerHTML = statusHtml(s, current.inventoryMode, root.dataset.estimatedShip);
      }

      if (fundedBadge) fundedBadge.classList.toggle('is-visible', s.secured);
      if (discSvg)     discSvg.classList.toggle('is-spinning', s.secured);

      // Sold out or failed means no more selling, regardless of campaign_active.
      if (preorderBtn) {
        preorderBtn.disabled = !s.sellable;
        preorderBtn.style.display = (campaignActive && s.sellable) ? '' : 'none';
      }
      if (btnText) {
        btnText.textContent = s.state === 'closed_sold_out' ? 'Sold out'
                            : s.secured ? 'Add to Cart — Pressing Confirmed'
                            : 'Pre-order & Reserve Your Copy';
      }
      // Cap the quantity stepper at what's actually left when the ceiling applies.
      if (current.inventoryMode !== 'oversell' && s.available > 0) {
        maxQty = maxQtyRaw > 0 ? Math.min(maxQtyRaw, s.available) : s.available;
      } else {
        maxQty = maxQtyRaw;
      }
      if (qty > maxQty && maxQty > 0) { qty = maxQty; if (qtyDisplay) qtyDisplay.textContent = qty; }
    }

    function campaignEnded() {
      if (!endIso) return false;
      return new Date(endIso).getTime() <= Date.now();
    }

    // Switching edition switches campaign: different break-even, different ceiling,
    // possibly a different state entirely.
    function selectVariant(id) {
      current = variantById(id);
      root.dataset.variantId = current.id;
      // Each edition has its own artwork and disc colours, so the mockup is rebuilt too.
      const pim  = root.querySelector('[data-pim]') || document.querySelector('[data-pim]');
      const hero = pim && pim.closest('.press-it__artwork-hero');
      if (pim && window.PressItMockup) {
        const cover = current.mockupCover || pim.dataset.cover || '';
        pim.dataset.cover = cover;
        pim.dataset.label = current.mockupLabel || '';
        pim.dataset.discConfig = current.discConfig ? JSON.stringify(current.discConfig) : '';
        delete pim.dataset.pimDone;   // render() is idempotent by design; allow a redraw
        if (current.discConfig) {
          window.PressItMockup.render(pim);
          if (hero) hero.classList.add('has-mockup');
        } else {
          // This edition has no disc config. render() only bails, so without this the
          // PREVIOUS edition's record would stay on screen next to the new edition's
          // name. Fall back to the flat cover instead.
          pim.innerHTML = '';
          if (cover) {
            const img = document.createElement('img');
            img.className = 'press-it__artwork-img';
            img.src = cover;
            img.alt = '';
            pim.appendChild(img);
          }
          if (hero) hero.classList.remove('has-mockup');
        }
      }
      if (priceEl && current.priceFormatted) priceEl.textContent = current.priceFormatted;
      qty = 1;
      if (qtyDisplay) qtyDisplay.textContent = '1';
      render(current.count);
      applyCampaignState();
    }

    // -- Campaign active/inactive UI ---------------------------
    function applyCampaignState() {
      if (!campaignActive) {
        if (preorderBtn)   preorderBtn.style.display = 'none';
        if (notifySection) notifySection.classList.add('is-visible');
        if (endedBanner) {
          endedBanner.classList.add('is-visible');
          // If there's an end date and it's in the future, campaign is not yet live
          const endMs = endIso ? new Date(endIso).getTime() : 0;
          endedBanner.textContent = (endMs > Date.now())
            ? 'Campaign not yet live -- sign up to be notified.'
            : 'This campaign has ended.';
        }
      } else {
        if (notifySection) notifySection.classList.remove('is-visible');
        if (endedBanner)   endedBanner.classList.remove('is-visible');
        if (preorderBtn)   preorderBtn.style.display = '';
      }
    }

    // -- Qty controls ------------------------------------------
    function updateQty(delta) {
      const next = qty + delta;
      if (next < 1) return;
      if (maxQty > 0 && next > maxQty) return; // 0 = unlimited
      qty = next;
      if (qtyDisplay) qtyDisplay.textContent = qty;
      if (qtyMinus)   qtyMinus.disabled = qty <= 1;
      if (qtyPlus)    qtyPlus.disabled  = maxQty > 0 && qty >= maxQty;
    }

    if (qtyMinus) qtyMinus.addEventListener('click', () => updateQty(-1));
    if (qtyPlus)  qtyPlus.addEventListener('click',  () => updateQty(+1));

    // -- Pre-order -> Shopify cart ------------------------------
    async function handlePreorder() {
      if (!variantId) { showToast('! No variant configured.', 'amber'); return; }
      if (preorderBtn) { preorderBtn.classList.add('is-loading'); preorderBtn.disabled = true; }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: qty,
            properties: {
              '_press_it_campaign': 'true',
              'Campaign': root.dataset.albumTitle || 'Vinyl Pre-order',
            }
          })
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Could not add to cart');
        }

        const copyWord = qty === 1 ? '1 copy' : qty + ' copies';
        showToast(copyWord + ' added to cart -- complete checkout to confirm your pre-order.', 'amber');

        document.dispatchEvent(new CustomEvent('press-it:cart-updated', {
          detail: { variantId, qty }
        }));

      } catch (err) {
        showToast('Error: ' + err.message, '');
        console.error('[Press It]', err);
      } finally {
        if (preorderBtn) { preorderBtn.classList.remove('is-loading'); preorderBtn.disabled = false; }
      }
    }

    if (preorderBtn) preorderBtn.addEventListener('click', handlePreorder);

    // -- Notify me ---------------------------------------------
    if (notifyBtn && notifyInput) {
      notifyBtn.addEventListener('click', () => {
        const email = notifyInput.value.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          showToast('Please enter a valid email address.', '');
          return;
        }
        document.dispatchEvent(new CustomEvent('press-it:notify-signup', {
          detail: { email, productHandle: root.dataset.productHandle }
        }));
        notifyInput.value = '';
        notifyBtn.textContent = 'Subscribed &#x2713;';
        notifyBtn.disabled = true;
        showToast("[OK] We'll notify you when this campaign launches!", 'green');
      });
    }

    // -- Toast -------------------------------------------------
    let toastTimer;
    function showToast(msg, color) {
      toast.textContent = msg;
      toast.className = 'press-it__toast' + (color ? ' press-it__toast--' + color : '');
      void toast.offsetWidth;
      toast.classList.add('is-visible');
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
    }

    // -- Edition switching -------------------------------------
    if (variantSelect) {
      variantSelect.addEventListener('change', () => selectVariant(variantSelect.value));
    }

    // -- Boot --------------------------------------------------
    // Boot from the SELECTED variant's count, not the product rollup — Liquid has
    // already painted this same state server-side, so nothing should visibly change.
    render(current.count);
    applyCampaignState();
    startCountdown();
  }

  function boot() {
    document.querySelectorAll(SEL.root).forEach(el => {
      if (!el.dataset.piInitialised) {
        el.dataset.piInitialised = '1';
        initPressIt(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    // Clear init flags so re-loaded sections boot fresh
    document.querySelectorAll('[data-press-it]').forEach(el => delete el.dataset.piInitialised);
    boot();
  });

})();